'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const stories = [
{
  quote: "I landed in Tokyo with two suitcases, a phone plan that didn't work, and a dorm key that opened the wrong room. By week three, I had a routine, three friends from four different countries, and a favorite ramen shop. By week twelve, I didn't want to go home.",
  attribution: 'Kenji T., Houston → Tokyo',
  detail: 'Waseda University · Fall 2024',
  image: "https://images.unsplash.com/photo-1581138967680-4d29a4a11193",
  imageAlt: 'Young man smiling in front of Tokyo cityscape',
  annotation: 'week 3 → everything changed',
  tag: 'IELTS 7.0 · D-2 Visa · 4 credits transferred'
},
{
  quote: "The IELTS prep forums said I needed 7.5. I got 6.5 and still got in. The thing nobody tells you is that every program has different minimums, and half of them will negotiate if your academic record is strong.",
  attribution: 'Fatima A., Nairobi → Berlin',
  detail: 'TU Berlin · Spring 2025',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec1bf7f0-1769340399494.png",
  imageAlt: 'Young woman studying at a desk with books and laptop',
  annotation: 'IELTS 6.5 was enough',
  tag: 'Erasmus+ grant · Full credit transfer · Staying for Masters'
},
{
  quote: "My housing deposit was €1,200 that I had to wire before I had a visa. The apartment fell through. I got the money back in six weeks. I slept on a hostel bunk for the first month and it was, genuinely, the best month of the year.",
  attribution: 'Lena M., São Paulo → Amsterdam',
  detail: 'University of Amsterdam · Fall 2024',
  image: "https://images.unsplash.com/photo-1694633810457-a5ef70e1066f",
  imageAlt: 'Young woman with backpack standing by a canal in Amsterdam',
  annotation: '€1,200 deposit — gone, then back',
  tag: 'MVV Visa · 78% credits · Housing: month 2 onward'
}];


export default function StudentVoicePanel() {
  const [active, setActive] = useState(0);
  const story = stories?.[active];

  return (
    <section className="scroll-panel" style={{ background: '#EDE8DF' }}>
      <div className="noise-overlay" />
      <div className="w-full h-full flex flex-col md:flex-row items-stretch pt-16 pb-12">

        {/* LEFT: Photo + caption */}
        <div
          className="flex-shrink-0 md:w-[36%] flex flex-col justify-center items-center px-8 md:px-10 py-8 md:py-10"
          style={{ borderRight: '1px solid rgba(184,175,166,0.4)' }}>
          
          <div className="panel-content w-full">
            {/* Photo */}
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <div
                style={{
                  width: '100%',
                  maxWidth: '260px',
                  margin: '0 auto',
                  aspectRatio: '4/5',
                  overflow: 'hidden',
                  border: '1px solid rgba(184,175,166,0.4)',
                  filter: 'grayscale(15%) sepia(8%)',
                  position: 'relative'
                }}>
                
                <AppImage
                  src={story?.image}
                  alt={story?.imageAlt}
                  fill
                  className="object-cover"
                  style={{ transition: 'opacity 0.4s ease' }} />
                
              </div>
              {/* Handwritten annotation */}
              <div style={{
                position: 'absolute',
                bottom: '-10px',
                right: '10px',
                background: '#fffef9',
                padding: '4px 10px',
                transform: 'rotate(-2deg)',
                border: '1px solid rgba(184,175,166,0.3)',
                boxShadow: '1px 2px 5px rgba(26,26,26,0.1)'
              }}>
                <span className="font-fraunces" style={{ fontSize: '11px', fontStyle: 'italic', color: 'var(--vermillion)' }}>
                  {story?.annotation}
                </span>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <div className="font-fraunces" style={{ fontSize: '14px', fontWeight: 500, color: 'var(--sumi)', marginBottom: '4px' }}>
                {story?.attribution}
              </div>
              <div className="section-label mb-4">{story?.detail}</div>
              <div
                className="font-plex-mono"
                style={{ fontSize: '9px', letterSpacing: '0.08em', color: 'var(--washi)', background: 'var(--sumi)', padding: '4px 10px', display: 'inline-block' }}>
                
                {story?.tag}
              </div>
            </div>

            {/* Story navigation */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
              {stories?.map((_, i) =>
              <button
                key={i}
                onClick={() => setActive(i)}
                style={{
                  width: active === i ? '20px' : '6px',
                  height: '6px',
                  borderRadius: active === i ? '3px' : '50%',
                  background: active === i ? 'var(--vermillion)' : 'var(--tatami)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.19, 1, 0.22, 1)',
                  padding: 0
                }} />

              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Quote + context */}
        <div className="flex-1 flex flex-col justify-center px-8 md:px-12 lg:px-16 py-8 md:py-10">
          <div className="panel-content">
            <div className="section-label mb-6">Chapter 04 — Voices</div>
            <hr className="tatami-rule mb-8" />

            {/* Large opening quote mark */}
            <svg width="32" height="24" viewBox="0 0 32 24" fill="none" style={{ marginBottom: '12px', opacity: 0.2 }}>
              <path d="M0 24V14C0 8 3 3 9 0L11 3C7 5 5 8 5 12H10V24H0ZM18 24V14C18 8 21 3 27 0L29 3C25 5 23 8 23 12H28V24H18Z" fill="var(--sumi)" />
            </svg>

            <blockquote
              className="font-fraunces"
              style={{ fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)', fontWeight: 400, color: 'var(--sumi)', lineHeight: 1.65, maxWidth: '540px', marginBottom: '32px', letterSpacing: '-0.01em' }}>
              
              {story?.quote}
            </blockquote>

            <hr className="tatami-rule mb-6" style={{ maxWidth: '200px' }} />

            <p className="font-dm-sans" style={{ fontSize: '13px', color: 'var(--sumi-light)', lineHeight: 1.7, maxWidth: '440px', fontWeight: 300 }}>
              These are not exceptional stories. They are the median experience — the version of study elite consultancy that university info sessions don't have time for, and Reddit threads only partially capture.
            </p>

            <div style={{ marginTop: '28px' }}>
              <p className="font-dm-sans" style={{ fontSize: '13px', color: 'var(--sumi-light)', lineHeight: 1.6 }}>
                The guide collects 23 more like this — organized by country, visa type, and outcome.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}